import { i18n } from '@/shared/i18n';
import { formatDateMask } from '../lib/dateMask';

/**
 * Selectable market + platform options for the taxonomy create/version forms.
 *
 * These are UI-side option lists until the backend exposes a managed
 * vocabulary. Values are stored verbatim on the version's `market` /
 * `platform` string arrays.
 */

/* eslint-disable i18next/no-literal-string --
 * Stored values, not UI copy. Markets and platforms are persisted on the
 * version and compared by value, and the platform names are vendor brands,
 * which do not translate.
 */
export const TAXONOMY_MARKET_OPTIONS = ['Global', 'NAMER', 'LATAM', 'EMEA', 'APAC'] as const;

export const TAXONOMY_PLATFORM_OPTIONS = [
  'Meta',
  'TikTok',
  'YouTube',
  'CM360',
  'DV360',
  'TTD',
  'Hulu',
  'CTV',
  'Google Ads',
  'LinkedIn',
  'Reddit',
] as const;
/* eslint-enable i18next/no-literal-string */

/**
 * Campaign-hierarchy level a field sits at (SME "guardrail"). This is a
 * SCHEMA-CONFIGURATION attribute only — it declares where a field belongs
 * (Campaign vs Ad Set vs Ad); the separate name-generation app consumes it.
 * `all` = a global field (e.g. Brand, Market) present at every level.
 * Stored verbatim on `TaxonomyField.level`.
 */
// Getters, not values: a resolved label here would freeze at import.
export const TAXONOMY_LEVEL_OPTIONS = [
  {
    // eslint-disable-next-line i18next/no-literal-string -- stored value
    value: 'all',
    get label() {
      return i18n.t('knowledge:taxonomy.common.levelAll');
    },
  },
  {
    // eslint-disable-next-line i18next/no-literal-string -- stored value
    value: 'campaign',
    get label() {
      return i18n.t('knowledge:taxonomy.common.levelCampaign');
    },
  },
  {
    value: 'ad_set',
    get label() {
      return i18n.t('knowledge:taxonomy.common.levelAdSet');
    },
  },
  {
    // eslint-disable-next-line i18next/no-literal-string -- stored value
    value: 'ad',
    get label() {
      return i18n.t('knowledge:taxonomy.common.levelAd');
    },
  },
] as const;

export type TaxonomyLevel = (typeof TAXONOMY_LEVEL_OPTIONS)[number]['value'];

/** Short display label for a stored `field.level` value (falls back to raw). */
export function taxonomyLevelLabel(level: string | null | undefined): string | null {
  if (!level) return null;
  return TAXONOMY_LEVEL_OPTIONS.find((o) => o.value === level)?.label ?? level;
}

/**
 * Re-exported, not redeclared — `@inference.engine/taxonomy` owns it, and its
 * `/web` entry is the ESM build this app must import from.
 *
 * There were three copies: this one, the backend's, and the library's. They had
 * already drifted once (`YYYYMMDD` here against `YYYY-MM-DD` in the picker),
 * which left the character counter two short per undated date field.
 */
export { DEFAULT_DATE_MASK } from '@inference.engine/taxonomy/web';

/**
 * Masks offered in the field editors. `format` stays a free string — these are
 * the common shapes, not an allow-list, so an unusual mask (e.g. `MMDD-MMDD`
 * for a flight window) is still typeable.
 */
/* eslint-disable i18next/no-literal-string -- masks and their sample output, which read the same in every language */
export const TAXONOMY_DATE_MASK_OPTIONS = [
  { value: 'YYYYMMDD', label: 'YYYYMMDD', example: '20260727' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2026-07-27' },
  { value: 'YYYYMM', label: 'YYYYMM', example: '202607' },
  { value: 'YYYY', label: 'YYYY', example: '2026' },
  { value: 'YYMMDD', label: 'YYMMDD', example: '260727' },
  { value: 'MMMYY', label: 'MMMYY', example: 'Jul26' },
  { value: 'MMDD', label: 'MMDD', example: '0727' },
] as const;
/* eslint-enable i18next/no-literal-string */

// Owned by `@inference.engine/taxonomy`, not mirrored here. It used to be a second
// literal with a comment conceding that the server's copy was the real one and this
// was "merely wrong about when to warn" if they diverged — which is a drift nothing
// could detect. Same treatment as `DEFAULT_DATE_MASK` above.
export { DEFAULT_TRACKING_CODE_CAP } from '@inference.engine/taxonomy/web';

/**
 * How full a schema has to be before its budget is worth mentioning.
 *
 * A guess, and knowingly so: nothing has ever counted minted codes, so there is
 * no distribution to pick a threshold from. Cheap to move once anyone has data.
 */
export const NEAR_CAP_FRACTION = 0.9;

/** The date the preset table's `example` strings were written against. */
export const TAXONOMY_DATE_MASK_SAMPLE = new Date(2026, 6, 27);

/**
 * Renders a mask against a fixed sample date, for previewing in the editors.
 *
 * Delegates rather than substituting here. This used to carry its own
 * YYYY/MMM/MM/DD/YY replace-chain beside the module that exists to stop exactly
 * that duplication — the class of drift that already made a cleared mask mean
 * "no characters" to the generator and "the full date" to the preview beside it.
 *
 * The preset fast-path went with it: `dateMask.test.ts` asserts
 * `formatDateMask(SAMPLE, value) === example` for every option, so looking the
 * answer up in the table and computing it can no longer disagree.
 */
export function taxonomyDateMaskExample(mask: string | null | undefined): string | null {
  const trimmed = mask?.trim();
  // Blank means "no mask", which has no preview — and must not reach
  // `formatDateMask`, whose blank fallback is the full default mask.
  if (!trimmed) return null;
  return formatDateMask(TAXONOMY_DATE_MASK_SAMPLE, trimmed);
}
